/**
 * Design System v2 (Phase 3) — Frontend helper module for the Template Designer
 * and Live Preview. Mirrors backend `services/template_dna.py` semantics so a
 * value shown in the Designer resolves the same way the storefront will render.
 *
 * Kept SEPARATE from `lib/designTokens.js` (legacy Live Preview parity util)
 * so we don't churn the older callers.
 */

// 10 canonical functional tokens — ORDERED for UI display.
export const CANONICAL_TOKEN_KEYS = [
  "primary",
  "primary_dark",
  "accent",
  "background",
  "surface",
  "surface_secondary",
  "text_primary",
  "text_secondary",
  "border",
  "sale",
];

// UI metadata per sprint §4 (labels + Indonesian helper text VERBATIM).
export const TOKEN_META = {
  primary:           { label: "Primary",           hint: "CTA dan elemen brand utama",          group: "brand" },
  primary_dark:      { label: "Primary Dark",      hint: "Hover dan pressed state dari Primary", group: "brand" },
  accent:            { label: "Accent",            hint: "Highlight dan aksen sekunder",         group: "brand" },
  background:        { label: "Background",        hint: "Latar belakang utama storefront",      group: "surfaces" },
  surface:           { label: "Surface / Card",    hint: "Card, modal dan panel utama",          group: "surfaces" },
  surface_secondary: { label: "Secondary Surface", hint: "Input dan area permukaan sekunder",   group: "surfaces" },
  text_primary:      { label: "Text Utama",        hint: "Heading dan teks utama",               group: "text" },
  text_secondary:    { label: "Text Sekunder",     hint: "Deskripsi dan informasi sekunder",     group: "text" },
  border:            { label: "Border",            hint: "Border, divider dan separator",        group: "detail" },
  sale:              { label: "Sale / Promo",      hint: "Diskon dan elemen promosi",            group: "detail" },
};

// UI groups (order matters — displayed top-to-bottom in the tab).
export const TOKEN_GROUPS = [
  { id: "brand",    title: "Brand",    keys: ["primary", "primary_dark", "accent"] },
  { id: "surfaces", title: "Surfaces", keys: ["background", "surface", "surface_secondary"] },
  { id: "text",     title: "Text",     keys: ["text_primary", "text_secondary"] },
  { id: "detail",   title: "Detail",   keys: ["border", "sale"] },
];

// System defaults — MUST stay in lockstep with backend
// `services/template_dna.py::CANONICAL_TOKEN_DEFAULTS`.
export const V2_DEFAULT_TOKENS = {
  primary:           "#0ea5e9",
  primary_dark:      "#0369a1",
  accent:            "#8b5cf6",
  background:        "#ffffff",
  surface:           "#ffffff",
  surface_secondary: "#f1f5f9",
  text_primary:      "#0f172a",
  text_secondary:    "#64748b",
  border:            "#e2e8f0",
  sale:              "#dc2626",
};

const LEGACY_TO_CANONICAL = {
  primary_color:    "primary",
  accent_color:     "accent",
  background_color: "background",
  text_color:       "text_primary",
  // secondary_color: intentionally NOT mapped (user-confirmed decision).
};

// ----- Strict hex -----------------------------------------------------------
const _STRICT_RE = /^#[0-9a-fA-F]{6}$/;

export function isStrictHex(v) {
  return typeof v === "string" && _STRICT_RE.test(v.trim());
}

export function normalizeStrictHex(v) {
  return typeof v === "string" ? v.trim().toLowerCase() : "";
}

// ----- primary_dark auto-derive (mirror of backend HSL -15%) ----------------
export function derivePrimaryDark(primaryHex) {
  if (!isStrictHex(primaryHex)) return V2_DEFAULT_TOKENS.primary_dark;
  const p = normalizeStrictHex(primaryHex);
  const r = parseInt(p.slice(1, 3), 16) / 255;
  const g = parseInt(p.slice(3, 5), 16) / 255;
  const b = parseInt(p.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;
  const d = max - min;
  if (d !== 0) {
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      default: h = (r - g) / d + 4; break;
    }
    h /= 6;
  }
  const l2 = Math.max(0, l - 0.15);
  const hue2rgb = (pp, qq, tt) => {
    if (tt < 0) tt += 1;
    if (tt > 1) tt -= 1;
    if (tt < 1 / 6) return pp + (qq - pp) * 6 * tt;
    if (tt < 1 / 2) return qq;
    if (tt < 2 / 3) return pp + (qq - pp) * (2 / 3 - tt) * 6;
    return pp;
  };
  let r2 = l2, g2 = l2, b2 = l2;
  if (s !== 0) {
    const q = l2 < 0.5 ? l2 * (1 + s) : l2 + s - l2 * s;
    const pp = 2 * l2 - q;
    r2 = hue2rgb(pp, q, h + 1 / 3);
    g2 = hue2rgb(pp, q, h);
    b2 = hue2rgb(pp, q, h - 1 / 3);
  }
  const to2 = (x) => Math.round(x * 255).toString(16).padStart(2, "0");
  return `#${to2(r2)}${to2(g2)}${to2(b2)}`;
}

// ----- Resolver -------------------------------------------------------------
/**
 * Compose 10 canonical tokens from a design object that may contain the new
 * canonical keys AND/OR legacy `*_color` keys. Missing tokens fall back to
 * V2_DEFAULT_TOKENS. `primary_dark` auto-derives from resolved `primary` when
 * absent. Returns lowercase strict hex.
 */
export function resolveTokens(design) {
  const src = (design && typeof design === "object") ? design : {};
  const out = {};

  for (const k of CANONICAL_TOKEN_KEYS) {
    if (isStrictHex(src[k])) out[k] = normalizeStrictHex(src[k]);
  }
  for (const [legacyK, canonicalK] of Object.entries(LEGACY_TO_CANONICAL)) {
    if (!(canonicalK in out) && isStrictHex(src[legacyK])) {
      out[canonicalK] = normalizeStrictHex(src[legacyK]);
    }
  }
  if (!("primary_dark" in out)) {
    out.primary_dark = derivePrimaryDark(out.primary || V2_DEFAULT_TOKENS.primary);
  }
  for (const k of CANONICAL_TOKEN_KEYS) {
    if (!(k in out)) out[k] = V2_DEFAULT_TOKENS[k];
  }
  return out;
}

// ----- WCAG contrast --------------------------------------------------------
function _srgbChannel(c) {
  const cc = c / 255;
  return cc <= 0.03928 ? cc / 12.92 : Math.pow((cc + 0.055) / 1.055, 2.4);
}

function _luminance(hex) {
  if (!isStrictHex(hex)) return 0.5;
  const p = normalizeStrictHex(hex);
  const r = _srgbChannel(parseInt(p.slice(1, 3), 16));
  const g = _srgbChannel(parseInt(p.slice(3, 5), 16));
  const b = _srgbChannel(parseInt(p.slice(5, 7), 16));
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export function contrastRatio(fg, bg) {
  const L1 = _luminance(fg);
  const L2 = _luminance(bg);
  const lighter = Math.max(L1, L2);
  const darker = Math.min(L1, L2);
  return (lighter + 0.05) / (darker + 0.05);
}

const AA_NORMAL = 4.5;   // WCAG 2.1 AA normal text.
const AA_LARGE  = 3.0;   // WCAG 2.1 AA large text / non-text UI (borders).

/**
 * Compute contrast advisory pairs per sprint §15 + Phase 2 expansions.
 * Returns array of { fgKey, bgKey, label, ratio } for pairs that FAIL the
 * relevant threshold. Non-blocking — advisory only.
 *
 * Text pairs use AA_NORMAL (4.5:1). Border-vs-background uses AA_LARGE (3:1)
 * because a divider is a non-text UI element (needs to be distinguishable,
 * not readable).
 */
export function contrastWarnings(tokens) {
  const pairs = [
    { fgKey: "text_primary",   bgKey: "background",        label: "Text Utama di atas Background",         threshold: AA_NORMAL },
    { fgKey: "text_primary",   bgKey: "surface",           label: "Text Utama di atas Surface",            threshold: AA_NORMAL },
    { fgKey: "text_primary",   bgKey: "surface_secondary", label: "Text Utama di atas Surface Secondary",  threshold: AA_NORMAL },
    { fgKey: "text_secondary", bgKey: "background",        label: "Text Sekunder di atas Background",      threshold: AA_NORMAL },
    { fgKey: "text_secondary", bgKey: "surface",           label: "Text Sekunder di atas Surface",         threshold: AA_NORMAL },
    { fgKey: "text_secondary", bgKey: "surface_secondary", label: "Text Sekunder di atas Surface Secondary", threshold: AA_NORMAL },
    { fg: "#ffffff",           bgKey: "primary",           label: "Text putih di atas Primary",            threshold: AA_NORMAL },
    { fgKey: "border",         bgKey: "background",        label: "Border terlihat di atas Background",    threshold: AA_LARGE  },
  ];
  const out = [];
  for (const p of pairs) {
    const fg = p.fg || tokens[p.fgKey];
    const bg = tokens[p.bgKey];
    if (!isStrictHex(fg) || !isStrictHex(bg)) continue;
    const ratio = contrastRatio(fg, bg);
    if (ratio < p.threshold) {
      out.push({
        fgKey: p.fgKey || null,
        bgKey: p.bgKey,
        label: p.label,
        ratio: Math.round(ratio * 100) / 100,
      });
    }
  }
  return out;
}
